/*!
 *   Copyright 2022-2025 SenX S.A.S.
 *
 *   Licensed under the Apache License, Version 2.0 (the "License");
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */

@import "../../scss/vars";
@import '../../../node_modules/@tarekraafat/autocomplete.js/dist/css/autoComplete.02.css';

:host {
  display: block;
  position: relative;
  height: 100%;
  width: 100%;

  & > div {
    height: 100%;
    width: 100%;
  }

  .hidden {
    display: none;
  }

  .chips-input-wrapper,
  input[type=text].discovery-input,
  input[type=password].discovery-input,
  input[type=date].discovery-input,
  input[type=time].discovery-input,
  input[type=number].discovery-input,
  textarea.discovery-input,
  input[type=file].discovery-input,
  select.discovery-input {
    margin: .25rem .125rem;
    display: inline-block;
    position: relative;
    font-size: var(--warp-view-input-font-size, 1rem);
    border-radius: var(--warp-view-input-border-radius, 0.25rem);
    border: 1px solid var(--warp-view-input-border-color, $w10-lightgrey);
    color: var(--warp-view-input-label-color, $w10-charcoal);
    background-color: var(--warp-view-input-bg-color, $w10-white);
    vertical-align: middle;
    width: 100%;
    height: 34px;
    padding: 1px 5px;

    &[disabled] {
      background-color: var(--warp-view-input-disabled-bg-color, $w10-lightgrey);
      color: var(--warp-view-input-disabled-label-color, $w10-charcoal);
      border: 1px solid var(--warp-view-input-disabled-border-color, $w10-lightgrey);
    }

    &:focus {
      outline: 0;
      box-shadow: 0 0 0 .25rem var(--warp-view-input-focus-outline-color, rgba(13, 110, 253, .25));
    }
  }

  input[type=file].discovery-input {
    height: 38px;
    padding: 0;
  }

  input.validation:invalid {
    outline: 1px solid $w10-danger !important;
    padding-right: calc(1.5em + .75rem);
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 12 12' width='12' height='12' fill='none' stroke='%23dc3545'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right calc(.375em + .1875rem) center;
    background-size: calc(.75em + .375rem) calc(.75em + .375rem);

    &:focus {
      border-color: $w10-danger;
      box-shadow: 0 0 0 .25rem rgba(220, 53, 69, .25);
    }
  }

  input.validation:valid {
    outline: 1px solid $w10-success !important;
    padding-right: calc(1.5em + .75rem);
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3e%3cpath fill='%23198754' d='M2.3 6.73.6 4.53c-.4-1.04.46-1.4 1.1-.8l1.1 1.4 3.4-3.8c.6-.63 1.6-.27 1.2.7l-4 4.6c-.43.5-.8.4-1.1.1z'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right calc(.375em + .1875rem) center;
    background-size: calc(.75em + .375rem) calc(.75em + .375rem);

    &:focus {
      border-color: $w10-success;
      box-shadow: 0 0 0 .25rem rgba(25, 135, 84, .25);
    }
  }

  textarea.discovery-input {
    width: calc(100% - 0.75rem);
    height: 100%;
  }

  .chips-input-wrapper {
    min-height: 34px;
    height: fit-content;

    &.disabled {
      background-color: var(--warp-view-input-disabled-bg-color, $w10-lightgrey);
      color: var(--warp-view-input-disabled-label-color, $w10-charcoal);
      border: 1px solid var(--warp-view-input-disabled-border-color, $w10-lightgrey);
    }
  }

  select.discovery-input {
    height: 38px;

    &[multiple] {
      height: 100%;
    }

    option {
      line-height: 2.5em;
    }
  }

  .flatpickr-wrapper {
    width: 100%;
    margin-right: 20px;
    align-items: center;
    justify-content: center;
    margin-bottom: 10px;

    .numInputWrapper span.arrowUp, .numInputWrapper span.arrowDown {
      height: 50%;
    }
  }

  .discovery-input-wrapper {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    height: 100%;

    &.type-date, &.type-date-range, &.type-autocomplete {
      height: auto;
    }

    &.type-textarea {
      flex-direction: column;
    }
  }

  .autoComplete_wrapper {
    width: 100%;
    margin-right: 20px;
    margin-bottom: 10px;
  }

  .slider-wrapper {
    height: 100%;
    width: calc(100% - 50px);
    padding-left: 10px;
    padding-right: 10px;
  }

  .multi-cb-wrapper {
    display: flex;
    flex-direction: column;
    height: 100%;
    position: absolute;
    width: 100%;

    .multi-cb-layout {
      flex: 1 1 auto;
      min-height: 0;
      padding-right: 17px;
      position: relative;
      display: flex;
      flex-direction: column;
    }

    .multi-cb-list-wrapper {
      overflow-y: auto;
      height: 100%;
      margin-right: -15px;
    }

    .multi-cb-buttons-wrapper {
      padding-top: 5px;
    }

    .multi-cb-buttons-wrapper {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }
  }


  button.discovery-btn, input[type=file]::file-selector-button {
    margin: .25rem .125rem;
    display: inline-block;
    font-weight: 400;
    line-height: 1.5em;
    text-align: center;
    text-decoration: none;
    vertical-align: middle;
    cursor: pointer;
    font-size: var(--warp-view-button-font-size, 1rem);
    border: 1px solid var(--warp-view-button-border-color, $w10-blue);
    padding: var(--warp-view-button-padding, .375rem .75rem);
    color: var(--warp-view-button-label-color, $w10-white);
    background-color: var(--warp-view-button-bg-color, $w10-blue);
    min-width: var(--warp-view-button-width, auto);
    border-radius: var(--warp-view-button-border-radius, 0.25rem);

    &.secondary {
      border: 1px solid var(--warp-view-button-secondary-border-color, $w10-grey);
      background-color: var(--warp-view-button-secondary-bg-color, $w10-grey);
      color: var(--warp-view-button-secondary-label-color, $w10-white);
    }
  }

  button:disabled, input[type=file][disabled]::file-selector-button {
    background-color: var(--warp-view-button-disabled-bg-color, $w10-lightgrey);
    color: var(--warp-view-button-disabled-label-color, $w10-charcoal);
    border: 1px solid var(--warp-view-button-disabled-border-color, $w10-lightgrey);
  }

  input[type=file]::file-selector-button {
    margin: 0 10px 0 0;
  }

  .discovery-input-btn-wrapper.slider {
    margin-top: 24px;
  }

  .discovery-input-btn-wrapper.textarea {
    text-align: end;
  }

  .range {
    display: flex;
    justify-content: start;
    align-items: center;
    flex-direction: row;
    margin: .25rem .125rem;
    font-size: var(--warp-view-input-font-size, 1rem);
    border-radius: var(--warp-view-input-border-radius, 0.25rem);
    border: 1px solid var(--warp-view-input-border-color, $w10-lightgrey);
    color: var(--warp-view-input-label-color, $w10-charcoal);
    background-color: var(--warp-view-input-bg-color, $w10-white);
    height: 34px;

    .flatpickr-wrapper {
      margin: 0;
    }

    width: 100%;
    padding: 1px 5px;

    span {
      padding-left: .25rem;
      padding-right: .25rem;
      font-size: var(--warp-view-input-font-size, 1rem);
      height: auto;

    }

    .discovery-input, .discovery-input:focus {
      border: none !important;
      margin: 0 !important;
      padding: 0 !important;
      max-height: 25px;
      cursor: pointer;
    }

    &.disabled {
      background-color: var(--warp-view-input-disabled-bg-color, $w10-lightgrey);
      color: var(--warp-view-input-disabled-label-color, $w10-charcoal);
      border: 1px solid var(--warp-view-input-disabled-border-color, $w10-lightgrey);
    }
  }
  .filtered-list-wrapper {
    width: 100%;
    

    .filtered-list-filter {
      width: calc(100% - 0.25rem - 12px);
      box-sizing: border-box;
    }

    .filtered-list-line {
      //width: calc(100% - 0.25rem - 12px);
      display: inline-flex;
      flex-flow: row;
      width: 100%;

      .filtered-list-list {
        //flex: 1;
        box-sizing: border-box;
        select {
          width: calc(100% - 0.25rem - 12px);
          height: 34px;
        }
      }

      //.discovery-input-btn-wrapper {
        //flex: 1 0 content;
      //}
    }

    input {
      box-sizing: content-box;
    }

    select {
      box-sizing: content-box;
    }
  }
  textarea {
    resize: none;
  }
}
